import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "More in this Cluster" widget — appears below the article body to power
 * tight topic-cluster internal linking. Renders nothing if the article has
 * no cluster or no sibling articles yet.
 */
export const ClusterRail = ({ postSlug }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/blog/${postSlug}/cluster`);
        if (!cancelled) setData(res.data);
      } catch {
        // Silently ignore — widget is non-critical
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [postSlug]);

  if (!data || !data.cluster || !data.articles || data.articles.length === 0) return null;

  return (
    <section
      className="mt-16 pt-16 border-t border-[#E5E0D8]"
      data-testid="cluster-rail"
    >
      <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[#E5E0D8]">
        <div>
          <p className="overline overline-gold mb-2">More in This Cluster</p>
          <h2 className="font-heading text-2xl lg:text-3xl text-[#111]">
            The <span className="italic">{data.cluster.name}</span> Hub
          </h2>
        </div>
        <Link
          to={`/clusters/${data.cluster.slug}`}
          className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border-b border-[#111] pb-1 hover:text-[#C69C6D] hover:border-[#C69C6D] transition-colors"
          data-testid="cluster-rail-hub-link"
        >
          View All
          <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
        </Link>
      </div>

      <ul className="space-y-px bg-[#E5E0D8]">
        {data.articles.map((a) => (
          <li key={a.slug}>
            <Link
              to={`/blog/${a.slug}`}
              className="bg-[#FAF9F6] flex items-center justify-between gap-6 p-5 lg:p-6 group hover:bg-white transition-colors"
              data-testid={`cluster-rail-${a.slug}`}
            >
              <div className="flex-1 min-w-0">
                <p className="overline mb-1.5">{a.category || 'Editorial'}</p>
                <h3 className="font-heading text-lg lg:text-xl text-[#111] leading-snug group-hover:text-[#C69C6D] transition-colors line-clamp-2">
                  {a.title}
                </h3>
              </div>
              <ArrowRight
                className="w-4 h-4 text-[#888] group-hover:text-[#C69C6D] group-hover:translate-x-1 transition-all flex-shrink-0"
                strokeWidth={1.5}
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ClusterRail;
